import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, ExternalLink, Filter, Package, Folder, Home, FileQuestion } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface TopLandingPagesTableProps {
  days: number
  section?: string
}

const URL_TYPE_ICONS: Record<string, any> = {
  product: Package,
  category: Folder,
  home: Home,
  cms: FileText,
  other: FileQuestion,
}

const URL_TYPE_LABELS: Record<string, string> = {
  product: 'Producto',
  category: 'Categoria',
  home: 'Inicio',
  cms: 'CMS',
  other: 'Otro',
}

const SOURCE_LABELS: Record<string, string> = {
  google_organic: 'Google',
  bing_organic: 'Bing',
  google_ads: 'Ads',
  direct: 'Directo',
  referral: 'Referencia',
}

export default function TopLandingPagesTable({ days, section }: TopLandingPagesTableProps) {
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['seo-top-landing-pages', days, sourceFilter, section],
    queryFn: () => seoAnalytics.getTopLandingPages(days, sourceFilter || undefined, 100, section),
  })

  // Get traffic overview for source list
  const overviewQuery = useQuery({
    queryKey: ['seo-traffic-overview', days, section],
    queryFn: () => seoAnalytics.getTrafficOverview(days, section),
  })

  const sources = overviewQuery.data?.data?.by_source?.map((s: any) => s.source) || []

  let pages = data?.data || []

  // Filter by type if selected
  if (typeFilter) {
    pages = pages.filter((p: any) => p.url_type === typeFilter)
  }

  // Get unique URL types from data
  const urlTypes = [...new Set(pages.map((p: any) => p.url_type))]

  // Truncate URL for display
  const truncateUrl = (url: string, maxLength: number = 60) => {
    if (url.length <= maxLength) return url
    return url.substring(0, maxLength) + '...'
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Top Landing Pages</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Paginas de entrada mas visitadas
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="input input-sm w-32"
          >
            <option value="">Todas</option>
            {sources.map((source: string) => (
              <option key={source} value={source}>
                {SOURCE_LABELS[source] || source}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input input-sm w-32"
          >
            <option value="">Todos tipos</option>
            {urlTypes.map((type) => (
              <option key={type as string} value={type as string}>
                {URL_TYPE_LABELS[type as string] || type}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : pages.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay datos de landing pages</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Visitas
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Unicos
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Fuentes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {pages.slice(0, 50).map((page: any, idx: number) => {
                  const TypeIcon = URL_TYPE_ICONS[page.url_type] || FileQuestion
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <a
                            href={`https://www.jvs-informatica.com${page.url_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                            title={page.url_path}
                          >
                            {truncateUrl(page.url_path)}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </a>
                        </div>
                        {page.prestashop_id && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            ID: {page.prestashop_id}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <TypeIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {URL_TYPE_LABELS[page.url_type] || page.url_type}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {page.visits?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {page.unique_visitors?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {page.sources?.split(',').slice(0, 3).map((source: string) => (
                            <span
                              key={source}
                              className="px-1.5 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                            >
                              {SOURCE_LABELS[source.trim()] || source.trim()}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Show count */}
        {pages.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-500">
            Mostrando {Math.min(50, pages.length)} de {data?.count || pages.length} paginas
          </div>
        )}
      </div>
    </div>
  )
}
